import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, Divider, TextField, MenuItem, Select, FormControl,
  InputLabel, Switch, FormControlLabel, Chip, Stack, Button, CircularProgress, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axios from '../../utils/axios';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchUnits } from '../../store/unitsStore/unitsThunk';
import { Utils } from '../../utils/common-utils';

// ---------------------------------------------------------------------------
// Real-world barcode label designer. Two panes in a right-side drawer:
//   left  = settings (which code to print, on-the-fly item-detail edits, field
//           toggles, layout, quantity)
//   right = live preview that renders the EXACT same markup used for printing.
// The barcode CODE comes from the saved mapping (chosen, not edited). The item
// DETAILS (name, MRP, sale price, unit, qty) can be overridden on the fly for this
// print only — the saved SKU is never touched. Barcode images render on demand from
// the backend `/erp/barcode/render` endpoint, so no backend change is needed.
// ---------------------------------------------------------------------------

export interface BarcodeLabelItem {
  _id: string;
  // When set, the barcode is chosen from this batch's mappings (internal_batch
  // barcode) instead of the SKU-level ones. `_id` stays the SKU id (for unit lookups).
  batchId?: string;
  name?: string;
  itemCode?: string;
  hsnCode?: string;
  mrp?: number;
  salePrice?: number;
  unitCode?: string;                             // legacy fallback (sale unit)
  // Units & Packs — base (stock) unit + packs (unitConversions keyed by MeasuringUnit id).
  baseUnit?: { _id?: string; name?: string; code?: string } | string;
  baseUnitName?: string;
  unitConversions?: Record<string, number>;
  barcodeMode?: string;
}

interface CompanyInfo { name?: string; phone?: string; customerCare?: string; }

interface Props {
  open: boolean;
  onClose: () => void;
  accountId?: string;
  item?: BarcodeLabelItem | null;
  companyInfo?: CompanyInfo;
}

interface PrintLayout {
  id: string; name: string; itemsPerPage: number;
  itemWidth: string; itemHeight: string; fontSize: string; thermal?: boolean;
}

const PRINT_LAYOUTS: PrintLayout[] = [
  { id: 'thermal_58mm', name: 'Thermal 58mm', itemsPerPage: 10, itemWidth: '58mm', itemHeight: '40mm', fontSize: '9pt', thermal: true },
  { id: 'thermal_80mm', name: 'Thermal 80mm', itemsPerPage: 8, itemWidth: '80mm', itemHeight: '45mm', fontSize: '10pt', thermal: true },
  { id: 'standard_labels', name: 'Standard 48mm (4-up)', itemsPerPage: 24, itemWidth: '48mm', itemHeight: '46mm', fontSize: '8pt' },
  { id: 'medium_labels', name: 'Medium 62mm (3-up)', itemsPerPage: 18, itemWidth: '62mm', itemHeight: '48mm', fontSize: '9pt' },
  { id: 'large_labels', name: 'Large 90mm (2-up)', itemsPerPage: 10, itemWidth: '90mm', itemHeight: '58mm', fontSize: '10pt' },
];

const QUANTITY_PRESETS = [12, 24, 36, 48, 60, 100];

// Editable (print-only) item details shown on the label.
interface LabelDetails { name: string; itemCode: string; mrp: number; salePrice: number; unit: string; qtyPerLabel: number; }

// Which fields the label shows. Persisted per account in localStorage.
interface LabelFields {
  company: boolean; name: boolean; itemCode: boolean; barcodeNumber: boolean;
  mrp: boolean; salePrice: boolean; discount: boolean; unit: boolean; qty: boolean;
  tax: boolean; care: boolean;
}

const DEFAULT_FIELDS: LabelFields = {
  company: false, name: true, itemCode: true, barcodeNumber: true,
  mrp: true, salePrice: true, discount: true, unit: true, qty: true,
  tax: false, care: false,
};

const FIELD_GROUPS: { title: string; items: { key: keyof LabelFields; label: string }[] }[] = [
  { title: 'Product', items: [
    { key: 'name', label: 'Product name' },
    { key: 'itemCode', label: 'Item code' },
    { key: 'barcodeNumber', label: 'Barcode number' },
  ] },
  { title: 'Pricing', items: [
    { key: 'mrp', label: 'MRP' },
    { key: 'salePrice', label: 'Sale price' },
    { key: 'discount', label: 'Discount (MRP − Sale)' },
  ] },
  { title: 'Packing', items: [
    { key: 'unit', label: 'Unit' },
    { key: 'qty', label: 'Qty line' },
  ] },
  { title: 'Extras', items: [
    { key: 'company', label: 'Company name' },
    { key: 'tax', label: '“Incl. all taxes”' },
    { key: 'care', label: 'Customer care' },
  ] },
];

// Prices on the label: 2-decimal max, grouped, no currency prefix (₹ is added inline).
const fmt = (v: number) => Utils.formatNumber(v || 0, false, true);
const round2 = (v?: number) => Math.round((v || 0) * 100) / 100;

const renderUrl = (code: string, symbology: string) => {
  const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/?$/, '/');
  return `${base}api/v1/erp/barcode/render?code=${encodeURIComponent(code)}&symbology=${encodeURIComponent(symbology)}`;
};

const escapeHtml = (s: string) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

// Shared CSS for both the on-screen preview and the print document. Class names are
// prefixed `bl-` so injecting them into the app can't collide with app styles.
const labelCss = (layout: PrintLayout) => `
  .bl-label {
    width: ${layout.itemWidth};
    /* Height hugs the content — toggled-off / empty fields leave no blank sticker space. */
    border: 1.5px solid #000; padding: 2mm; background: #fff;
    display: flex; flex-direction: column; box-sizing: border-box;
    font-family: Arial, sans-serif; font-size: ${layout.fontSize}; line-height: 1.2;
    break-inside: avoid; page-break-inside: avoid; flex-shrink: 0;
  }
  .bl-company { font-weight: 700; text-transform: uppercase; text-align: center;
    font-size: ${layout.fontSize}; margin-bottom: 0.5mm; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .bl-name { text-align: center; font-weight: 600; margin: 0.5mm 0; line-height: 1.15;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; word-break: break-word; }
  .bl-bc { display: flex; flex-direction: column; align-items: center; margin: 1mm 0; }
  .bl-bc img { height: ${layout.fontSize === '8pt' ? '8mm' : layout.fontSize === '9pt' ? '10mm' : '12mm'}; width: auto; max-width: 96%; object-fit: contain; }
  .bl-bc-num { font-size: 8pt; font-weight: 700; letter-spacing: .5px; margin-top: .5mm; }
  .bl-details { font-size: 7pt; margin-top: 1mm; }
  .bl-row { display: flex; justify-content: space-between; align-items: center; gap: 4px; margin: .4mm 0; white-space: nowrap; }
  .bl-strong { font-weight: 700; }
  .bl-disc { color: #b91c1c; font-weight: 700; }
  .bl-muted { color: #444; font-style: italic; }
  .bl-noimg { border: 1px dashed #999; color: #999; font-size: 6pt; padding: 2mm; text-align: center; }
`;

// Inner markup for ONE label (no outer .bl-label wrapper).
const labelInnerHtml = (f: LabelFields, d: LabelDetails, code: string, symbology: string, company: CompanyInfo): string => {
  const mrp = d.mrp || 0;
  const sale = d.salePrice || 0;
  const discount = mrp - sale;
  const unit = d.unit || 'Unit';
  const qty = d.qtyPerLabel || 1;

  const parts: string[] = [];
  if (f.company && company.name) parts.push(`<div class="bl-company">${escapeHtml(company.name)}</div>`);
  if (f.name && d.name) parts.push(`<div class="bl-name">${escapeHtml(d.name)}</div>`);

  parts.push(
    `<div class="bl-bc">` +
    (code
      ? `<img src="${renderUrl(code, symbology)}" alt="barcode" />` +
        (f.barcodeNumber ? `<div class="bl-bc-num">${escapeHtml(code)}</div>` : '')
      : `<div class="bl-noimg">No barcode</div>`) +
    `</div>`
  );

  const rows: string[] = [];
  const left: string[] = [];
  if (f.qty) left.push(`Qty: ${escapeHtml(String(qty))}${f.unit ? ` ${escapeHtml(unit)}` : ''}`);
  else if (f.unit) left.push(`Unit: ${escapeHtml(unit)}`);
  if (f.itemCode && d.itemCode) left.push(`Code: ${escapeHtml(d.itemCode)}`);

  // Row 1: qty/unit  |  MRP
  if (left.length || f.mrp) {
    rows.push(
      `<div class="bl-row"><span>${left[0] || ''}</span>` +
      `${f.mrp ? `<span class="bl-strong">MRP: ₹${fmt(mrp)}</span>` : '<span></span>'}</div>`
    );
  }
  // Row 2: sale price  |  incl taxes
  if (f.salePrice || f.tax) {
    rows.push(
      `<div class="bl-row">${f.salePrice ? `<span class="bl-strong">Rate: ₹${fmt(sale)}</span>` : '<span></span>'}` +
      `${f.tax ? `<span class="bl-muted">(Incl. all taxes)</span>` : '<span></span>'}</div>`
    );
  }
  // Row 3: discount
  if (f.discount && discount > 0) {
    const pct = mrp > 0 ? Math.round((discount / mrp) * 100) : 0;
    rows.push(`<div class="bl-row"><span class="bl-disc">You save: ₹${fmt(discount)}${pct ? ` (${pct}%)` : ''}</span></div>`);
  }
  // Extra item code row when qty is off and code wasn't shown in row 1
  if (left.length > 1) rows.push(`<div class="bl-row"><span>${left[1]}</span></div>`);
  // Care
  if (f.care && (company.customerCare || company.phone)) {
    rows.push(`<div class="bl-row"><span>C.care: ${escapeHtml(company.customerCare || company.phone || '')}</span></div>`);
  }

  if (rows.length) parts.push(`<div class="bl-details">${rows.join('')}</div>`);
  return parts.join('');
};

const storageKey = (accountId?: string) => `barcodeLabelSettings:${accountId || 'default'}`;

const BarcodeLabelDrawer: FC<Props> = ({ open, onClose, accountId, item, companyInfo = {} }) => {
  const dispatch = useAppDispatch();
  const unitsById = useAppSelector((state) => state.units.byId);
  const unitsList = useAppSelector((state) => state.units.units);

  const [mappings, setMappings] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [code, setCode] = useState('');           // barcode to print (chosen from the saved mappings, not edited)
  const [symbology, setSymbology] = useState('CODE128');

  // Editable, print-only item details (defaults from the SKU; never saved back).
  const [details, setDetails] = useState<LabelDetails>({ name: '', itemCode: '', mrp: 0, salePrice: 0, unit: '', qtyPerLabel: 1 });

  const [fields, setFields] = useState<LabelFields>(DEFAULT_FIELDS);
  const [layout, setLayout] = useState<PrintLayout>(PRINT_LAYOUTS[0]);
  const [quantity, setQuantity] = useState<number>(24);
  const [customQty, setCustomQty] = useState('');

  const [printing, setPrinting] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);

  // Load persisted settings (fields + layout + qty) once per account.
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(storageKey(accountId));
      if (raw) {
        const s = JSON.parse(raw);
        if (s.fields) setFields({ ...DEFAULT_FIELDS, ...s.fields });
        if (s.layoutId) {
          const l = PRINT_LAYOUTS.find((x) => x.id === s.layoutId);
          if (l) setLayout(l);
        }
        if (typeof s.quantity === 'number') setQuantity(s.quantity);
      }
    } catch { /* ignore corrupt cache */ }
  }, [open, accountId]);

  // Persist on change (only while open, so the initial load above doesn't fight it).
  useEffect(() => {
    if (!open) return;
    try {
      localStorage.setItem(storageKey(accountId), JSON.stringify({ fields, layoutId: layout.id, quantity }));
    } catch { /* quota / private mode — non-fatal */ }
  }, [open, accountId, fields, layout.id, quantity]);

  // Load the units master once (needed to name pack units, which are stored as ids).
  useEffect(() => {
    if (open && (!unitsList || unitsList.length === 0)) dispatch(fetchUnits());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Base (stock) unit label from the item alone (no master lookup needed).
  const baseUnitLabel = useMemo(() => {
    if (!item) return '';
    const b = typeof item.baseUnit === 'object' ? item.baseUnit : null;
    return (b?.code || b?.name || item.baseUnitName || item.unitCode || '').trim();
  }, [item]);

  // Selectable units for the label: base unit + every pack unit (unitConversions).
  const unitOptions = useMemo(() => {
    const opts: string[] = [];
    const push = (s?: string) => { const v = (s || '').trim(); if (v && !opts.includes(v)) opts.push(v); };
    push(baseUnitLabel);
    Object.keys(item?.unitConversions || {}).forEach((id) => push(unitsById?.[id]?.code || unitsById?.[id]?.name));
    push(details.unit); // keep the current selection selectable
    return opts;
  }, [baseUnitLabel, item, unitsById, details.unit]);

  // Seed the editable details from the item whenever a new item opens.
  useEffect(() => {
    if (!open || !item) return;
    setDetails({
      name: item.name || '',
      itemCode: item.itemCode || '',
      // Seed to 2 decimals — the label prints 2dp, so keep the editable inputs in sync.
      mrp: round2(item.mrp),
      salePrice: round2(item.salePrice),
      unit: baseUnitLabel,
      qtyPerLabel: 1,
    });
  }, [open, item?._id, item?.batchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the barcode mappings on open — by batch when a batch context is given
  // (internal_batch barcode), otherwise the SKU-level mappings.
  useEffect(() => {
    if (!open || !item?._id) return;
    setLoading(true);
    const query = item.batchId ? `batchId=${item.batchId}` : `skuId=${item._id}`;
    axios.get(`/api/v1/erp/barcode/mappings?${query}`)
      .then((res) => {
        const rows = (Array.isArray(res.data) ? res.data : []).filter((m: any) => m.isActive);
        setMappings(rows);
        const primary = rows.find((m: any) => m.isPrimary) || rows[0];
        if (primary) {
          setSelectedId(primary._id);
          setCode(primary.code);
          setSymbology(primary.symbology || 'CODE128');
        } else {
          setSelectedId(''); setCode(''); setSymbology('CODE128');
        }
      })
      .catch(() => setMappings([]))
      .finally(() => setLoading(false));
  }, [open, item?._id, item?.batchId]);

  // Switching the selected mapping refreshes the code + symbology.
  const onSelectMapping = (id: string) => {
    setSelectedId(id);
    const m = mappings.find((x) => x._id === id);
    if (m) { setCode(m.code); setSymbology(m.symbology || 'CODE128'); }
  };

  const setField = (key: keyof LabelFields, val: boolean) => setFields((prev) => ({ ...prev, [key]: val }));
  const setDetail = <K extends keyof LabelDetails>(key: K, val: LabelDetails[K]) =>
    setDetails((prev) => ({ ...prev, [key]: val }));

  const handleQty = (n: number) => { setQuantity(n); setCustomQty(''); };
  const handleCustomQty = (v: string) => {
    setCustomQty(v);
    const n = parseInt(v, 10);
    if (!isNaN(n) && n > 0) setQuantity(n);
  };

  const innerHtml = useMemo(
    () => labelInnerHtml(fields, details, code, symbology, companyInfo),
    [fields, details, code, symbology, companyInfo]
  );
  const css = useMemo(() => labelCss(layout), [layout]);

  const buildPrintHtml = useCallback((): string => {
    const perPage = layout.itemsPerPage;
    const total = Math.max(1, quantity);
    const one = labelInnerHtml(fields, details, code, symbology, companyInfo);
    const labels = Array.from({ length: total }, () => `<div class="bl-label">${one}</div>`);
    const pages: string[] = [];
    for (let i = 0; i < labels.length; i += perPage) {
      pages.push(`<div class="bl-page">${labels.slice(i, i + perPage).join('')}</div>`);
    }
    return `<!DOCTYPE html><html><head><meta charset="utf-8" />
      <title>Barcode - ${escapeHtml(details.name || '')}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        @page { margin: ${layout.thermal ? '2mm 0 0 2mm' : '5mm'}; size: ${layout.thermal ? `${layout.itemWidth} auto` : 'A4'}; }
        body { font-family: Arial, sans-serif; ${layout.thermal ? `width:${layout.itemWidth};` : ''} }
        .bl-page { display: ${layout.thermal ? 'block' : 'flex'}; ${layout.thermal ? '' : 'flex-wrap: wrap;'} gap: 2mm; page-break-after: ${layout.thermal ? 'auto' : 'always'}; }
        .bl-page:last-child { page-break-after: avoid; }
        ${layout.thermal ? '.bl-label { margin: 2mm 0; }' : ''}
        ${css}
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style></head><body>${pages.join('')}</body></html>`;
  }, [fields, details, code, symbology, companyInfo, layout, quantity, css]);

  const doPrint = useCallback((setBusy: (b: boolean) => void) => {
    setBusy(true);
    try {
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;left:-9999px;';
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow?.document;
      if (!doc) { setBusy(false); return; }
      doc.open(); doc.write(buildPrintHtml()); doc.close();
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          setTimeout(() => { document.body.removeChild(iframe); setBusy(false); }, 500);
        }, 400); // let barcode images load
      };
    } catch {
      setBusy(false);
    }
  }, [buildPrintHtml]);

  const previewCount = Math.min(quantity, 8);
  const noBarcode = !loading && mappings.length === 0;

  // Scale preview labels so exactly 3 fit the pane width — true proportions, no scroll.
  const MM_PX = 96 / 25.4;
  const PREVIEW_COLS = 3;
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [previewW, setPreviewW] = useState(0);
  useEffect(() => {
    if (!open || loading) return;
    const el = previewRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => setPreviewW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, loading]);

  const labelWpx = parseFloat(layout.itemWidth) * MM_PX;
  const gapPx = 2 * MM_PX;
  const avail = previewW - gapPx * (PREVIEW_COLS - 1);
  const previewScale = previewW > 0 ? Math.min(1, avail / (PREVIEW_COLS * labelWpx)) : 1;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', md: 940 }, maxWidth: '100vw' } }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eef0f3' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Print barcode label</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#9da7b7' }}>{item?.name || 'Item'}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ---------------- Settings pane ---------------- */}
        <Box sx={{ width: 380, flexShrink: 0, borderRight: '1px solid #eef0f3', overflowY: 'auto', p: 2.5 }}>
          {noBarcode ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This item has no barcode yet. Add or generate one from the item’s edit page first.
            </Alert>
          ) : null}

          {/* Barcode chooser */}
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Barcode</Typography>
          {mappings.length > 1 ? (
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Which code</InputLabel>
              <Select label="Which code" value={selectedId} onChange={(e) => onSelectMapping(e.target.value)}>
                {mappings.map((m) => (
                  <MenuItem key={m._id} value={m._id}>
                    {m.code} {m.isPrimary ? '• Primary' : ''} {m.batchId ? '• Batch' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          {/* Selected code (read-only — chosen, not edited) */}
          {code ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Chip size="small" label={symbology} variant="outlined" />
              <Typography sx={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600 }}>{code}</Typography>
            </Box>
          ) : null}

          <Divider sx={{ my: 2 }} />

          {/* Editable item details (print-only override) */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Details</Typography>
            <Typography sx={{ fontSize: 10.5, color: '#9da7b7' }}>for this print only</Typography>
          </Box>
          <TextField
            size="small" fullWidth label="Product name" value={details.name}
            onChange={(e) => setDetail('name', e.target.value)} sx={{ mb: 1.5 }}
          />
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <TextField
              size="small" fullWidth type="number" label="MRP (₹)" value={details.mrp}
              onChange={(e) => setDetail('mrp', Number(e.target.value) || 0)} inputProps={{ min: 0 }}
            />
            <TextField
              size="small" fullWidth type="number" label="Sale price (₹)" value={details.salePrice}
              onChange={(e) => setDetail('salePrice', Number(e.target.value) || 0)} inputProps={{ min: 0 }}
            />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
            {unitOptions.length > 0 ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select label="Unit" value={details.unit} onChange={(e) => setDetail('unit', e.target.value)}>
                  {unitOptions.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </Select>
              </FormControl>
            ) : (
              <TextField
                size="small" fullWidth label="Unit" value={details.unit}
                onChange={(e) => setDetail('unit', e.target.value)}
              />
            )}
            <TextField
              size="small" fullWidth type="number" label="Qty on label" value={details.qtyPerLabel}
              onChange={(e) => setDetail('qtyPerLabel', Number(e.target.value) || 1)} inputProps={{ min: 1 }}
            />
          </Stack>
          {details.mrp - details.salePrice > 0 ? (
            <Typography sx={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 600, mt: 0.5 }}>
              Discount: ₹{fmt(details.mrp - details.salePrice)}
              {details.mrp > 0 ? ` (${Math.round(((details.mrp - details.salePrice) / details.mrp) * 100)}%)` : ''}
            </Typography>
          ) : null}

          <Divider sx={{ my: 2 }} />

          {/* Field toggles */}
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>Show on label</Typography>
          {FIELD_GROUPS.map((g) => (
            <Box key={g.title} sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 11, color: '#9da7b7', textTransform: 'uppercase', letterSpacing: 0.4, mt: 1 }}>{g.title}</Typography>
              {g.items.map((it) => (
                <FormControlLabel
                  key={it.key}
                  sx={{ display: 'flex', ml: 0, justifyContent: 'space-between', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                  labelPlacement="start"
                  control={<Switch size="small" checked={fields[it.key]} onChange={(e) => setField(it.key, e.target.checked)} />}
                  label={it.label}
                />
              ))}
            </Box>
          ))}

          <Divider sx={{ my: 2 }} />

          {/* Layout */}
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Label size</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Layout</InputLabel>
            <Select
              label="Layout" value={layout.id}
              onChange={(e) => setLayout(PRINT_LAYOUTS.find((l) => l.id === e.target.value) || PRINT_LAYOUTS[0])}
            >
              {PRINT_LAYOUTS.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </Select>
          </FormControl>

          {/* Quantity */}
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>Quantity</Typography>
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 1, mb: 1.5 }}>
            {QUANTITY_PRESETS.map((p) => (
              <Chip
                key={p} label={p} size="small"
                color={quantity === p && !customQty ? 'primary' : 'default'}
                variant={quantity === p && !customQty ? 'filled' : 'outlined'}
                onClick={() => handleQty(p)}
              />
            ))}
          </Stack>
          <TextField
            size="small" type="number" label="Custom qty" value={customQty}
            onChange={(e) => handleCustomQty(e.target.value)} inputProps={{ min: 1, max: 10000 }} sx={{ width: 150 }}
          />
        </Box>

        {/* ---------------- Preview pane ---------------- */}
        <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#f4f6f8', overflowY: 'auto', p: 3 }}>
          <style dangerouslySetInnerHTML={{ __html: css }} />
          <Typography sx={{ fontSize: 12.5, color: '#7a8290', mb: 2 }}>
            Live preview — showing {previewCount} of {quantity} labels
          </Typography>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={26} /></Box>
          ) : (
            <Box
              ref={previewRef}
              sx={{ display: 'grid', gridTemplateColumns: `repeat(${PREVIEW_COLS}, max-content)`, gap: `${gapPx}px`, justifyContent: 'start', alignItems: 'start' }}
            >
              {Array.from({ length: previewCount }).map((_, i) => (
                // `zoom` scales the layout footprint (unlike transform), so each label's
                // row auto-sizes to its content — no fixed-height cell, no blank space.
                <div
                  key={i}
                  className="bl-label"
                  style={{ zoom: previewScale } as any}
                  dangerouslySetInnerHTML={{ __html: innerHtml }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eef0f3', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button color="inherit" onClick={onClose}>Cancel</Button>
        <Button
          variant="outlined"
          startIcon={savingPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
          disabled={savingPdf || printing || noBarcode}
          onClick={() => doPrint(setSavingPdf)}
        >
          Save as PDF
        </Button>
        <Button
          variant="contained"
          startIcon={printing ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PrintIcon />}
          disabled={printing || savingPdf || noBarcode}
          onClick={() => doPrint(setPrinting)}
        >
          Print Now
        </Button>
      </Box>
    </Drawer>
  );
};

export default BarcodeLabelDrawer;
